import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Package, Truck, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dispatch() {
  const [params] = useSearchParams();
  const [key, setKey] = useState(() => params.get("key") || localStorage.getItem("tabi_dispatch_key") || "");
  const [orders, setOrders] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async (k) => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { params: { key: k } });
      setOrders(data);
      setDenied(false);
      localStorage.setItem("tabi_dispatch_key", k);
    } catch {
      setDenied(true);
      setOrders(null);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  return (
    <div data-testid="dispatch-page" className="min-h-screen bg-ink px-6 py-16 text-neutral-200">
      <div className="mx-auto max-w-3xl">
        <p className="mb-4 text-center font-mono text-[11px] uppercase tracking-[0.5em] text-gold">Private</p>
        <h1 className="text-center font-serif text-4xl font-light text-neutral-100">Dispatch</h1>
        <p className="mt-3 text-center text-sm font-light text-neutral-500">
          Enter a tracking number when you post an order — the customer is emailed automatically.
        </p>

        {denied && (
          <p data-testid="dispatch-denied" className="mt-10 text-center text-sm text-red-400">
            Incorrect access key. Check your bookmarked link.
          </p>
        )}

        {!orders && !denied && (
          <form
            className="mx-auto mt-12 flex max-w-md gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
          >
            <input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Access key"
              data-testid="dispatch-key-input"
              className="w-full border-b border-white/25 bg-transparent py-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-gold focus:outline-none"
            />
            <button
              type="submit"
              data-testid="dispatch-key-submit"
              className="shrink-0 border border-gold/40 px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-gold-light hover:bg-gold/10"
            >
              Open
            </button>
          </form>
        )}

        {orders && (
          <div className="mt-12 space-y-6">
            {orders.length === 0 && (
              <p className="text-center text-sm text-neutral-500">No paid orders yet.</p>
            )}
            {orders.map((o) => (
              <OrderCard key={o.session_id} order={o} adminKey={key} onDone={() => load(key)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, adminKey, onDone }) {
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("royal_mail");
  const [busy, setBusy] = useState(false);
  const shipped = !!order.shipped;
  const refunded = order.payment_status === "refunded";

  const ship = async () => {
    if (!tracking.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ship`, {
        key: adminKey,
        session_id: order.session_id,
        tracking_number: tracking,
        carrier,
      });
      toast.success("Marked as shipped — customer emailed.");
      onDone();
    } catch {
      toast.error("Could not mark as shipped.");
      setBusy(false);
    }
  };

  return (
    <div data-testid={`dispatch-order-${order.session_id.slice(-8)}`} className="border border-white/10 bg-white/[0.02] p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-serif text-xl font-light text-neutral-100">
            <Package className="h-4 w-4 text-gold-light" strokeWidth={1.5} />
            {order.product_name || order.lookup_key}
          </p>
          <p className="mt-1 font-mono text-xs text-neutral-500">
            £{((order.amount || 0) / 100).toFixed(2)} · {new Date(order.created_at).toLocaleDateString("en-GB")}
          </p>
        </div>
        {refunded && (
          <span className="border border-red-400/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/90">
            Refunded
          </span>
        )}
        {shipped && !refunded && (
          <span className="flex items-center gap-1.5 border border-gold/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">
            <Check className="h-3 w-3" strokeWidth={2} /> Shipped
          </span>
        )}
      </div>
      <div className="mt-4 text-sm font-light leading-relaxed text-neutral-400">
        <p>{order.customer_name || "—"} · {order.customer_email || "—"}</p>
        <p data-testid="dispatch-address">{order.shipping_address || "—"}</p>
      </div>

      {shipped || refunded ? (
        <p className="mt-4 font-mono text-xs text-neutral-500">
          {order.carrier} · {order.tracking_number}
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <select
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            data-testid="dispatch-carrier-select"
            className="border border-white/15 bg-ink px-3 py-3 text-sm text-neutral-200 focus:border-gold focus:outline-none"
          >
            <option value="royal_mail">Royal Mail</option>
            <option value="evri">Evri</option>
            <option value="dpd">DPD</option>
            <option value="other">Other courier</option>
          </select>
          <input
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="Tracking number"
            data-testid="dispatch-tracking-input"
            className="w-full border-b border-white/25 bg-transparent py-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-gold focus:outline-none"
          />
          <button
            onClick={ship}
            disabled={busy || !tracking.trim()}
            data-testid="dispatch-ship-button"
            className="flex shrink-0 items-center justify-center gap-2 border border-gold/40 px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-gold-light hover:bg-gold/10 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Truck className="h-3.5 w-3.5" strokeWidth={1.5} />}
            Ship & email
          </button>
        </div>
      )}
    </div>
  );
}
